import { useState, type ReactNode } from 'react';
import { useAppSelector } from 'app/store';
import { selectUserProfile } from 'app/store/userProfileSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useTranslation } from 'react-i18next';
import { ReactComponent as BackArrow } from 'assets/BackArrow.svg';
import { ReactComponent as SwitchCompanyIcon } from 'assets/SwitchCompanyIcon.svg';
import { MOBILE_HEADER_HEIGHT } from 'app/pages/mobile/utils/constants';
import { Box, Typography, IconButton, ButtonBase, styled } from '@mui/material';
import { ReactComponent as SpeedchainMobileLogo } from 'assets/SpeedchainMobileLogo.svg';
import MobileCompanySwitcher from 'app/pages/mobile/components/mobile-company-switcher/MobileCompanySwitcher';
import { useCanSwitchCompany } from 'app/pages/mobile/hooks/useCanSwitchCompany';

const HeaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  background: 'transparent',

  padding: '16px',
  height: MOBILE_HEADER_HEIGHT,
  justifyContent: 'center',
});

const LogoSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const WelcomeSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const TitleSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const CompanyLine = styled(ButtonBase)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  alignSelf: 'flex-start',
  color: theme.palette.common.white,
  // Cancel most of the header's 16px flex gap so the company line hugs the
  // title/welcome as a subtitle (matching Figma) and the extra height stays
  // small enough not to clip the logo.
  marginTop: '-14px',
}));

interface MobileHeaderProps {
  showLogo?: boolean;
  centerTitle?: boolean;
  customTitle?: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  headerRight?: ReactNode;
  // Top-level tab pages opt in to the company line + switcher under the header.
  showCompanySwitcher?: boolean;
}

const MobileHeader = ({
  showLogo = false,
  customTitle,
  showBackButton = false,
  centerTitle = false,
  onBackClick,
  headerRight,
  showCompanySwitcher = false,
}: MobileHeaderProps) => {
  const { t } = useTranslation();
  const { profile } = useAppSelector(selectUserProfile);
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  const canSwitch = useCanSwitchCompany();
  const [switcherOpen, setSwitcherOpen] = useState(false);

  // AC (SEE-1220): the switcher only shows for users who can actually switch —
  // members of 2+ companies (support users included, matching the web switcher).
  // Single-company users get the plain header, no company line.
  const showCompanyLine = showCompanySwitcher && canSwitch && !!selectedPaidol?.name;

  const companyLine = showCompanyLine ? (
    <CompanyLine onClick={() => setSwitcherOpen(true)} aria-label={t('mobileCompanySwitcherAction')}>
      <Box component={SwitchCompanyIcon} sx={{ height: 16, width: 16 }} />
      <Typography variant="small" sx={{ color: 'common.white', fontWeight: 500 }}>
        {selectedPaidol?.name}
      </Typography>
    </CompanyLine>
  ) : null;

  return (
    // On switcher pages, top-align the header so the company line grows
    // downward into the empty space below (the dark region is taller than the
    // fixed header box) instead of re-centering and pushing the logo off-screen.
    <HeaderContainer sx={showCompanyLine ? { justifyContent: 'flex-start' } : undefined}>
      {showLogo && (
        <>
          <LogoSection>
            <SpeedchainMobileLogo
              style={{
                height: '18px',
                width: 'auto',
                filter: 'brightness(0) invert(1)',
              }}
            />
          </LogoSection>

          <WelcomeSection>
            <Typography
              variant="h2"
              sx={{
                color: 'common.white',
                fontWeight: 600,
              }}
            >
              {profile?.first_name
                ? t('mobileHeader.welcomeNamed', { name: profile?.first_name })
                : t('mobileHeader.welcome')}
            </Typography>
          </WelcomeSection>
          {companyLine}
        </>
      )}

      {customTitle && (
        <TitleSection
          sx={{
            justifyContent: headerRight || (centerTitle && showBackButton) ? 'space-between' : 'flex-start',
          }}
        >
          {showBackButton && (
            <IconButton
              onClick={onBackClick}
              aria-label="Back"
              sx={{
                color: 'white',
                padding: 0,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <BackArrow style={{ height: '24px', width: 'auto' }} />
            </IconButton>
          )}
          <Typography
            variant="h2"
            sx={{
              color: 'common.white',
              fontWeight: 600,
            }}
            style={{ textAlign: centerTitle ? 'center' : 'left' }}
          >
            {customTitle}
          </Typography>
          {headerRight ?? (centerTitle && showBackButton && <Box sx={{ p: 1.5 }} />)}
        </TitleSection>
      )}

      {customTitle && companyLine}

      {showCompanyLine && (
        <MobileCompanySwitcher open={switcherOpen} onClose={() => setSwitcherOpen(false)} />
      )}
    </HeaderContainer>
  );
};

export default MobileHeader;
